// Formules de la plateforme (super-admin) : lots de modules à prix unique, application à un client, canaux (widget, Google Maps…).
// Serveur : functions/api/plateforme-formules.js. Cadrage : docs/zenchef-formules/AUDIT-FORMULES-ZENCHEF.md.
import { apiFetch } from './api';

export type CanalId = 'widget' | 'google' | 'meta' | 'annuaire' | 'telephone';
/** 0 à faire · 1 guide envoyé · 2 en attente du client · 3 vérifié */
export type StatutCanal = 0 | 1 | 2 | 3;
export const LIBELLES_STATUT_CANAL: Record<number, string> = { 0: 'À faire', 1: 'Guide envoyé', 2: 'En attente du client', 3: 'Vérifié' };

export interface Formule {
    id: string;
    nom: string;
    description: string;
    prix_eur: number;
    sms_inclus: number;
    minutes_tel: number;
    frais_carte_pct: number;
    /** Type de client auquel la formule s'adresse. */
    type_client: string;
    /** Salariés compris dans la formule ; au-delà, la grille de paliers de l'équipe s'applique avec la remise ci-dessous. */
    salaries_inclus: number;
    remise_equipe_pct: number;
    /** Quotas compris dans la formule (0 = non défini ; pour les conversations, 0 = valeur par défaut de la plateforme). */
    avis_inclus: number;
    emails_inclus: number;
    conversations_incluses: number;
    /** Tout ce que la formule donne : modules seuls + modules de ses blocs. */
    modules: string[];
    /** Les modules cochés en dehors des blocs. */
    modules_seuls: string[];
    /** Les blocs de modules (noms) compris dans la formule. */
    blocs: string[];
    canaux: CanalId[];
    ordre: number;
    actif: boolean;
}
export interface FormuleAvecMarge extends Formule {
    clients: number;
    cout_eur: number;
    marge_eur: number;
    marge_pct: number;
    /** Ce que coûteraient les mêmes modules pris un par un. */
    somme_carte_eur: number;
    economie_eur: number;
}
export interface ModuleCatalogue { name: string; label: string; famille: string; statut: 'disponible' | 'bientot'; core: boolean; types?: string[] }
export interface BlocFormule { name: string; label: string; pilote: string; membres: string[] }
export interface ListeFormules { formules: FormuleAvecMarge[]; modules: ModuleCatalogue[]; blocs: BlocFormule[]; canaux: { id: CanalId; label: string }[]; couts: Record<string, number> }
export type CorpsFormule = Pick<Formule, 'nom' | 'description' | 'prix_eur' | 'sms_inclus' | 'minutes_tel' | 'frais_carte_pct' | 'type_client' | 'salaries_inclus' | 'remise_equipe_pct' | 'avis_inclus' | 'emails_inclus' | 'conversations_incluses' | 'modules' | 'blocs' | 'canaux'>;

export interface CanalClient { id: CanalId; label: string; inclus: boolean; statut: StatutCanal; donnees: Record<string, any>; updated_at: string }
export interface VueFormuleClient {
    client: { id: string; nom: string; type: string };
    formule: (Formule & { debut: string }) | null;
    canaux: CanalClient[];
    liens: { reservation: string; google: string; meta: string; widget: string };
}
export interface MatriceCanaux { clients: { client_id: string; nom: string; formule: string; canaux: Record<string, StatutCanal | null> }[]; canaux: { id: CanalId; label: string }[] }

export const formulesApi = {
    liste: (): Promise<ListeFormules> => apiFetch('/admin/formules/formules'),
    creer: (b: CorpsFormule): Promise<{ id: string; formules: FormuleAvecMarge[] }> => apiFetch('/admin/formules/formules', { method: 'POST', body: JSON.stringify(b) }),
    modifier: (id: string, b: CorpsFormule): Promise<{ formules: FormuleAvecMarge[] }> => apiFetch(`/admin/formules/formules/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
    supprimer: (id: string): Promise<{ formules: FormuleAvecMarge[] }> => apiFetch(`/admin/formules/formules/${id}`, { method: 'DELETE' }),
    couts: (c: Record<string, number>): Promise<{ couts: Record<string, number>; formules: FormuleAvecMarge[] }> => apiFetch('/admin/formules/couts', { method: 'PUT', body: JSON.stringify(c) }),
    client: (clientId: string): Promise<VueFormuleClient> => apiFetch(`/admin/formules/clients/${clientId}`),
    appliquer: (clientId: string, formule_id: string): Promise<{ modules_actives: string[]; modules_ignores: string[] }> =>
        apiFetch(`/admin/formules/clients/${clientId}/formule`, { method: 'POST', body: JSON.stringify({ formule_id }) }),
    retirer: (clientId: string): Promise<{ ok: boolean }> => apiFetch(`/admin/formules/clients/${clientId}/formule`, { method: 'DELETE' }),
    canal: (clientId: string, canal: CanalId, statut: StatutCanal, donnees: Record<string, any> = {}) =>
        apiFetch(`/admin/formules/clients/${clientId}/canaux/${canal}`, { method: 'PUT', body: JSON.stringify({ statut, donnees }) }),
    verifierWidget: (clientId: string, url: string): Promise<{ trouve: boolean; erreur?: string }> => apiFetch(`/admin/formules/clients/${clientId}/verifier-widget`, { method: 'POST', body: JSON.stringify({ url }) }),
    matrice: (): Promise<MatriceCanaux> => apiFetch('/admin/formules/matrice'),
};

// --- Demandes de changement de formule ---
export interface DemandeFormule { id: string; client_id: string; client: string; formule_id: string; formule_nom: string; formule_actuelle: string | null; message: string; created_at: string }

export const clientOffertApi = {
    definir: (clientId: string, offert: boolean, motif = ''): Promise<{ ok: boolean; offert: boolean }> => apiFetch(`/admin/formules/clients/${clientId}/offert`, { method: 'PUT', body: JSON.stringify({ offert, motif }) }),
};

export const typesGratuitsApi = {
    lire: (): Promise<{ types: string[] }> => apiFetch('/admin/formules/types-gratuits'),
    enregistrer: (types: string[]): Promise<{ ok: boolean; types: string[] }> => apiFetch('/admin/formules/types-gratuits', { method: 'PUT', body: JSON.stringify({ types }) }),
};

export const demandesFormuleApi = {
    lister: (): Promise<{ demandes: DemandeFormule[] }> => apiFetch('/admin/formules/demandes'),
    accepter: (id: string): Promise<{ ok: boolean; modules_ignores: string[] }> => apiFetch(`/admin/formules/demandes/${id}/accepter`, { method: 'POST', body: '{}' }),
    refuser: (id: string, reponse: string) => apiFetch(`/admin/formules/demandes/${id}/refuser`, { method: 'POST', body: JSON.stringify({ reponse }) }),
};

// --- « Ma formule » côté client ---
export interface FormuleVueClient { id: string; nom: string; description: string; prix_eur: number; sms_inclus: number; minutes_tel: number; salaries_inclus?: number; remise_equipe_pct?: number; modules: string[]; canaux: string[]; debut?: string }
export interface MaFormule {
    formule: FormuleVueClient | null;
    formules: FormuleVueClient[];
    demande: { id: string; formule_id: string; formule_nom: string; message: string; created_at: string } | null;
    derniere_refusee: { formule_nom: string; reponse: string } | null;
}

export interface LimiteSalaries {
    module: string; limite: number | null; utilises: number; libres: number | null; prix_eur: number; franchise: number | null;
    paliers: { quantite: number; prix_eur: number }[];
    /** 'degressif' : nombre libre de salariés, prix = somme des tranches (voir shared/tarifDegressif.js). */
    mode?: 'paliers' | 'degressif';
    tranches?: { jusqu_a: number | null; prix_unite: number }[];
    salaries_inclus?: number;
    remise_equipe_pct?: number;
    demande: { id: string; quantite: number; message: string; created_at: string } | null;
    refus: { motif: string; date: string } | null;
}

export const maFormuleApi = {
    campagnes: (): Promise<LimiteSalaries> => apiFetch('/ma-formule/campagnes'),
    demanderCampagnes: (quantite: number, message: string) => apiFetch('/ma-formule/demande-campagnes', { method: 'POST', body: JSON.stringify({ quantite, message }) }),
    salaries: (): Promise<LimiteSalaries> => apiFetch('/ma-formule/salaries'),
    demanderSalaries: (quantite: number, message: string) => apiFetch('/ma-formule/demande-salaries', { method: 'POST', body: JSON.stringify({ quantite, message }) }),
    lire: (): Promise<MaFormule> => apiFetch('/ma-formule'),
    demander: (formule_id: string, message: string) => apiFetch('/ma-formule/demande', { method: 'POST', body: JSON.stringify({ formule_id, message }) }),
    annuler: () => apiFetch('/ma-formule/demande', { method: 'DELETE' }),
};
